/**
 * What the Getting started panel and the Setup page show, worked out from the setup-status
 * response and what the viewer may do. Pure (no React), so both screens agree and it is easy to
 * test.
 */

import type {
  ModuleKey,
  Permission,
  SetupFocusArea,
  SetupStatus,
  SetupStepKey,
} from '@hrms/shared';
import { SETUP_FOCUS_AREAS } from '@hrms/shared';
import { coreSteps, FOCUS_AREAS, moreSteps, SETUP_STEPS } from '../setupSteps.js';

/** The query key every setup-status reader shares (Home, Setup page, sidebar, welcome). */
export const SETUP_STATUS_KEY = ['setup-status'] as const;

/** "Hide for now" on the panel: a week, per company. */
export const HIDE_PANEL_DAYS = 7;
/** "Dismiss" on the "You're set up" state: as long as the API allows. */
export const DISMISS_DONE_DAYS = 30;

/** Used when a step has no blocked hint of its own. */
export const DEFAULT_BLOCKED_HINT = 'Someone with access to this screen has to do it.';

export type Can = (permission: Permission) => boolean;

/** Any-of: true when the viewer holds at least one of the permissions. */
export function canAny(can: Can, needs: Permission | readonly Permission[]): boolean {
  const list: readonly Permission[] = typeof needs === 'string' ? [needs] : needs;
  return list.some((permission) => can(permission));
}

export interface StepState {
  key: SetupStepKey;
  done: boolean;
  /** The viewer holds the permission the step's screen needs. */
  permitted: boolean;
}

export interface SetupProgress {
  /** The counted steps (normally six), in order. */
  core: StepState[];
  /** Every other applicable step, in checklist order. */
  more: StepState[];
  coreDone: number;
  coreTotal: number;
  allCoreDone: boolean;
  /** The step to suggest next: the first undone core step the viewer may do, else the first undone one. */
  next: StepState | null;
  focusAreas: SetupFocusArea[];
}

export function deriveSetupProgress(status: SetupStatus, can: Can): SetupProgress {
  const applicable = new Set<SetupStepKey>(
    status.steps.filter((step) => step.applicable).map((step) => step.key),
  );
  const done = new Set<SetupStepKey>(
    status.steps.filter((step) => step.done).map((step) => step.key),
  );
  const focusAreas = status.preferences.focusAreas;
  const toState = (key: SetupStepKey): StepState => ({
    key,
    done: done.has(key),
    permitted: canAny(can, SETUP_STEPS[key].needs),
  });

  const coreKeys = coreSteps(focusAreas, applicable);
  const core = coreKeys.map(toState);
  const more = moreSteps(coreKeys, applicable).map(toState);
  const coreDone = core.filter((step) => step.done).length;
  const undone = core.filter((step) => !step.done);

  return {
    core,
    more,
    coreDone,
    coreTotal: core.length,
    allCoreDone: undone.length === 0,
    next: undone.find((step) => step.permitted) ?? undone[0] ?? null,
    focusAreas,
  };
}

/** True while "Hide for now" (or "Dismiss") is still in force. */
export function isPanelHidden(hiddenUntil: string | null, now: number = Date.now()): boolean {
  if (!hiddenUntil) return false;
  const until = Date.parse(hiddenUntil);
  return Number.isFinite(until) && until > now;
}

export type FocusAreaAccess = 'open' | 'not_on_plan' | 'no_access';

/** Whether a picked area's link can be followed: its module is on and the viewer may open it. */
export function focusAreaAccess(
  area: SetupFocusArea,
  enabledModules: readonly ModuleKey[] | undefined,
  can: Can,
): FocusAreaAccess {
  const info = FOCUS_AREAS[area];
  // No list means every module is on (older sessions; same rule as the rest of the app).
  if (enabledModules && !enabledModules.includes(info.module)) return 'not_on_plan';
  return canAny(can, info.needs) ? 'open' : 'no_access';
}

/** All six areas, the picked ones first in the order picked. */
export function areasPickedFirst(picked: readonly SetupFocusArea[]): SetupFocusArea[] {
  return [...picked, ...SETUP_FOCUS_AREAS.filter((area) => !picked.includes(area))];
}

/** A 403 from ts-rest arrives either as the response or as the thrown error, depending on version. */
export function isForbidden(data: unknown, error: unknown): boolean {
  const status = (value: unknown) =>
    typeof value === 'object' && value !== null
      ? (value as { status?: unknown }).status
      : undefined;
  return status(data) === 403 || status(error) === 403;
}
